import React from "react";
import {Box, Button, Grid, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useFirmwareUploadMutation} from "../../../app/api/api";
import * as yup from "yup";
import {useFormik} from "formik";
import {useSnackbar} from "notistack";
import {FirmwareField} from "../../../app/api/model/firmware";
import DefaultDialog, {DefaultDialogSize} from "../../../components/Dialog/ClosableDialog";
import {UseModal} from "../../../hooks/useModal";

export interface FirmwareUploadFileDialogParams {
    firmwareId: number;
    modal: UseModal;
}

export default function FirmwareUploadFileDialog({firmwareId, modal}: FirmwareUploadFileDialogParams) {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [uploadFirmware] = useFirmwareUploadMutation();

    const validationSchema = yup.object({
        file: yup.mixed(),
        [FirmwareField.CHECKSUM]: yup.string().required(t("validation_inputRequired")),
    });

    const formik = useFormik({
        initialValues: {
            file: undefined,
            [FirmwareField.CHECKSUM]: "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const formData = new FormData();

            if (values[FirmwareField.CHECKSUM] && values.file) {
                formData.append("file", values.file);
                formData.append(FirmwareField.CHECKSUM, values[FirmwareField.CHECKSUM]);
            }

            await uploadFirmware({id: firmwareId, formData})
                .unwrap()
                .then(() => {
                    enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
                    handleClose();
                });
        },
    });

    const handleClose = () => {
        formik.resetForm();
        modal.onClose();
    };

    return (
        <DefaultDialog
            size={DefaultDialogSize.EXTRA_LARGE}
            open={modal.isOpen}
            titleKey={"action_uploadFirmware"}
            onClose={handleClose}
            fullWidth={true}
        >
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2} columns={{sm: 12, md: 12}} sx={{mt: 3}}>
                    <Grid item xs={12}>
                        <input
                            type={"file"}
                            id="file"
                            name="name"
                            onChange={(e) => {
                                if (!e.currentTarget.files) {
                                    return;
                                } else {
                                    const file = e.currentTarget.files[0];
                                    if (file) {
                                        formik.setFieldValue("file", file);
                                    }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            id={FirmwareField.CHECKSUM}
                            name={FirmwareField.CHECKSUM}
                            label={t("firmware_checksum")}
                            value={formik.values[FirmwareField.CHECKSUM]}
                            onChange={formik.handleChange}
                            error={
                                formik.touched[FirmwareField.CHECKSUM] && Boolean(formik.errors[FirmwareField.CHECKSUM])
                            }
                            helperText={formik.touched[FirmwareField.CHECKSUM] && formik.errors[FirmwareField.CHECKSUM]}
                            required={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{display: "flex", justifyContent: "flex-start"}}>
                            <Button color="primary" variant="contained" type="submit" disabled={!formik.dirty}>
                                {t("action_upload")}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </DefaultDialog>
    );
}
